import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Check, Star, ChevronRight } from "lucide-react";

// URL da sua API Strapi (ajuste se o Strapi estiver em outro endereço/porta)
//const API_BASE_URL = "http://localhost:1337/api"; // Exemplo: Strapi rodando localmente na porta 1337
const API_BASE_URL = "https://cms.webnorte.com.br/api";

// A estrutura de resposta do Strapi v4 para relações é aninhada em 'data' e 'attributes'.
// Ex: { data: [{ id: 1, attributes: { name: "...", whatsapp: "...", plans: { data: [...] } } }] }

//em caso de falha
const FALLBACK_CITIES = [
  {
    name: "Nova União",
    whatsapp: "5569981274593",
    plans: [
      { id: 1, name: "Plano Start", speed: "250", popular: false, exclusiveBenefits: [] },
      { id: 2, name: "Plano Turbo", speed: "450", popular: false, exclusiveBenefits: [{ id: 101, benefits: "WebnorTV ou Roteador Mesh" }] },
      { id: 3, name: "Plano Premium", speed: "650", popular: true, exclusiveBenefits: [{ id: 102, benefits: "Incluso WebnorTV" }, { id: 103, benefits: "Roteador Mesh" }] },
      { id: 4, name: "Plano Gamer", speed: "1000", popular: false, exclusiveBenefits: [{ id: 104, benefits: "Incluso WebnorTV" }, { id: 105, benefits: "Roteador Mesh" }] },
    ]
  },
  {
    name: "Vale do Paraíso",
    whatsapp: "5569984333306",
    plans: [
      { id: 5, name: "Plano Start", speed: "250", popular: false, exclusiveBenefits: [] },
      { id: 6, name: "Plano Turbo", speed: "450", popular: false, exclusiveBenefits: [{ id: 106, benefits: "WebnorTV ou Roteador Mesh" }] },
      { id: 7, name: "Plano Premium", speed: "650", popular: true, exclusiveBenefits: [{ id: 107, benefits: "Incluso WebnorTV" }, { id: 108, benefits: "Roteador Mesh" }] },
      { id: 8, name: "Plano Gamer", speed: "1000", popular: false, exclusiveBenefits: [{ id: 109, benefits: "Incluso WebnorTV" }, { id: 110, benefits: "Roteador Mesh" }] },
    ]
  }
];

const FALLBACK_BENEFITS = [
  "Fibra óptica 100%",
  "Suporte técnico rápido",
  "Roteador Wi-Fi incluso",
  "Instalação grátis"
];

export default function PlansSection() {
  const [citiesData, setCitiesData] = useState([]);
  const [globalBenefits, setGlobalBenefits] = useState([]);
  const [selectedCityName, setSelectedCityName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3500);

      try {
        // Strapi v4 usa 'populate' para incluir dados de relações
        const [citiesResponse, benefitsResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/cities?populate[plans][populate]=benefit_exclusives`, { signal: controller.signal }),
          fetch(`${API_BASE_URL}/global-benefits`, { signal: controller.signal }),
        ]);

        clearTimeout(timeoutId);

        if (!citiesResponse.ok) throw new Error(`HTTP error! status: ${citiesResponse.status}`);
        if (!benefitsResponse.ok) throw new Error(`HTTP error! status: ${benefitsResponse.status}`);

        const citiesJson = await citiesResponse.json();
        const benefitsJson = await benefitsResponse.json();

        // Adicione isso temporariamente:
        /* console.log("CITIES:", JSON.stringify(citiesJson, null, 2));
        console.log("BENEFITS:", JSON.stringify(benefitsJson, null, 2)); */
        // Mapeia a resposta do Strapi para um formato mais fácil de usar no componente
        const formattedCities = citiesJson.data.map(cityItem => ({
          name: cityItem.name,
          whatsapp: cityItem.whatsapp,
          plans: cityItem.plans.map(plan => ({
            ...plan,
            exclusiveBenefits: plan['benefit_exclusives'] || []
          }))
        }));
        const formattedBenefits = benefitsJson.data
          .map(b => b.text)
          .filter(Boolean);// Adiciona optional chaining e filtra valores nulos/undefined

        setCitiesData(formattedCities);
        setGlobalBenefits(formattedBenefits);
        if (formattedCities.length > 0) {
          setSelectedCityName(formattedCities[0].name); // Seleciona a primeira cidade por padrão
        }
      } catch (e) {
        console.error("Failed to fetch data:", e);
        setCitiesData(FALLBACK_CITIES);
        setGlobalBenefits(FALLBACK_BENEFITS);
        if (FALLBACK_CITIES.length > 0) {
          setSelectedCityName(FALLBACK_CITIES[0].name);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const selectedCityData = citiesData.find(c => c.name === selectedCityName) || { whatsapp: "", plans: [] };
  const plans = selectedCityData.plans;

  if (loading) return <div className="text-center py-20">Carregando planos...</div>;
  if (error) return <div className="text-center py-20 text-red-500">{error}</div>;
  if (citiesData.length === 0) return <div className="text-center py-20 text-muted-foreground">Nenhuma cidade ou plano disponível.</div>;

  return (
    <section id="planos" className="py-20 lg:py-28 relative bg-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
            Escolha o <span className="text-primary">plano ideal</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Selecione sua cidade e encontre o melhor plano para você
          </p>
        </motion.div>

        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {citiesData.map((city) => (
            <button
              key={city.name}
              onClick={() => setSelectedCityName(city.name)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${selectedCityName === city.name // Atualizado para usar gradiente Tailwind
                ? "bg-primary text-white shadow-lg shadow-primary/30"
                : "glass-card text-muted-foreground hover:text-foreground hover:border-primary/30"
                }`}
            >
              {city.name}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={selectedCityName}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.4 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {plans.map((plan, i) => (
              <motion.div
                key={plan.name + selectedCityName}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`relative rounded-2xl p-6 flex flex-col transition-all duration-300 hover:-translate-y-2 ${plan.popular 
                  ? "border-2 border-primary shadow-2xl shadow-primary/20 md:scale-105 z-10 bg-primary/[0.03]" 
                  : "border-2 border-slate-200 shadow-md bg-white"
                  }`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-primary text-white text-xs font-bold"> {/* Usando cor solida do tema */}
                      <Star className="h-3 w-3" /> Mais Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6 pt-2">
                  <p className="text-sm text-muted-foreground font-medium mb-2">{plan.name}</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-poppins font-extrabold text-5xl text-foreground">
                      {plan.speed}
                    </span>
                    <span className="text-primary font-bold text-lg">MEGA</span>
                  </div>
                </div>

                {/* <div className="text-center mb-6">
                  <span className="text-sm text-muted-foreground">R$ </span>
                  <span className="font-poppins font-bold text-3xl text-foreground">{plan.price}</span>
                  <span className="text-sm text-muted-foreground">/mês</span>
                </div> */}

                <div className="space-y-3 mb-8 flex-1">
                  {/* Benefícios globais (todos os planos) */}
                  {globalBenefits.map((b) => (
                    <div key={b} className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground">{b}</span>
                    </div>
                  ))}
                  {/* Benefícios exclusivos do plano */}
                  {plan.exclusiveBenefits.map((b) => (
                    <div key={b.id} className="flex items-center gap-2">
                      <div className="h-5 w-5 rounded-full bg-success/20 flex items-center justify-center flex-shrink-0">
                        <Check className="h-3 w-3 text-success" />
                      </div>
                      <span className="text-sm font-medium text-foreground">{b.benefits}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={`https://api.whatsapp.com/send?phone=${selectedCityData.whatsapp}&text=Olá! Quero contratar o ${plan.name} - ${plan.speed}MB em ${selectedCityName}!`}
                  target="_blank"
                  rel="noopener noreferrer"
                > {/* Atualizado para usar gradiente Tailwind */}
                  <Button
                    className={`w-full font-bold py-5 transition-colors ${plan.popular
                      ? "bg-primary text-white hover:opacity-90"
                      : "bg-primary/10 text-primary hover:bg-primary hover:text-white"
                      }`}
                  >
                    Contratar Agora
                  </Button>
                </a>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}